import {
  capitalize,
  flatten,
  flattenDeep,
  isEmpty,
  startCase,
  uniq,
} from 'lodash';
import {
  Condition,
  CrmType,
  FinanceType,
  FNIType,
  TradeInType,
  CreditRatingMax,
} from '@upstart/harmony/lib/legos/constants';
import { DMSType } from '@upstart/harmony/lib/legos/dmsTypes';
import {
  Days,
  Dealer,
  FinanceTypePricingValidation,
  PricingValidationClientType,
  PricingValidationType,
} from '../../types/dealerTypes';
import { CHAIRMAN_ROUTES, ROUTES } from '../../utils/routes';
import {
  basisToMoneyDecimalConversion,
  getNewInstorePriceSource,
  getUsedInstorePriceSource,
} from '../../utils/pricing';
import { DealerLaunchSection, DealerLaunchStep } from './types';
import { createMultiStep, createSubStep } from './DealerLaunchMultiStep';
// eslint-disable-next-line import/no-cycle
import { DealerLaunchSectionOverview } from './pages/DealerLaunchSectionOverview/DealerLaunchSectionOverview';
// eslint-disable-next-line import/no-cycle
import { DealerLaunchHome } from './pages/DealerLaunchHome';
import { DealerLaunchProductSelect } from './pages/DealerLaunchProductSelect';
import { DealerLaunchAboutInfo } from './pages/DealerLaunchAboutInfo';
import { DealerLaunchAboutContact } from './pages/DealerLaunchAboutContact';
import { DealerLaunchOnlinePickupDelivery } from './pages/DealerLaunchOnlinePickupDelivery';
// eslint-disable-next-line import/no-cycle
import { DealerLaunchReview } from './pages/DealerLaunchReview';
import { DealerLaunchInfoGate } from './pages/DealerLaunchInfoGate';
import {
  DealerLaunchIntegrationInventory,
  DealerLaunchIntegrationInventoryInfoGate,
} from './pages/DealerLaunchIntegrationInventory';
import {
  DealerLaunchIntegrationCRM,
  DealerLaunchIntegrationCRMInfoGate,
} from './pages/DealerLaunchIntegrationCRM';
import {
  DealerLaunchIntegrationFnI,
  DealerLaunchIntegrationFnIInfoGate,
} from './pages/DealerLaunchIntegrationFnI';
import {
  DealerLaunchIntegrationTradeinManager,
  DealerLaunchIntegrationTradeinManagerInfoGate,
} from './pages/DealerLaunchIntegrationTradeinManager';
import { DealerLaunchOnlineWidgets } from './pages/DealerLaunchOnlineWidgets';
import { DealerLaunchInstoreUsers } from './pages/DealerLaunchInstoreUsers';
import { formatHour } from '../../utils/format';
import {
  CRM_TYPE_NAME,
  DMS_TYPE_NAME,
  FNI_TYPE_NAME,
  INVENTORY_TYPE_NAME,
  PRICE_SOURCE_NAME,
  TRADEIN_MANAGER_TYPE_NAME,
} from '../../utils/constants';
import { DealerLaunchComplete } from './pages/DealerLaunchComplete';
import { DealerLaunchPricingFees } from './pages/DealerLaunchPricingFees';
import { DealerLaunchPricingSource } from './pages/DealerLaunchPricingSource';
import {
  DealerLaunchFinancePricingCredit,
  DealerLaunchLeasePricingCredit,
} from './pages/DealerLaunchPricingCredit';
import { DealerLaunchFinancePricingRates } from './pages/DealerLaunchFinancePricingRates';
import { DealerLaunchPricingRatesLease } from './pages/DealerLaunchPricingRatesLease';
import { DealerLaunchPricingValidation } from './pages/DealerLaunchPricingValidation';
import { DealerLaunchAboutSupportEmail } from './pages/DealerLaunchAboutSupportEmail';
import { DealerLaunchOnlineDealNotification } from './pages/DealerLaunchOnlineDealNotification';
import { DealerLaunchOnlineVirtualShowroom } from './pages/DealerLaunchOnlineVirtualShowroom';
import { DealerLaunchInstoreForms } from './pages/DealerLaunchInstoreForms';
import { DealerLaunchIntegrationADFEmail } from './pages/DealerLaunchIntegrationADFEmail';
import { DealerLaunchDeskingModel } from './pages/DealerLaunchDeskingModel';
import { DealerLaunchTradeInTaxCredit } from './pages/DealerLaunchTradeInTaxCredit';
import { DealerLaunchMaximumResidual } from './pages/DealerLaunchMaximumResidual';
import { DealerLaunchApplyRebates } from './pages/DealerLaunchApplyRebates';
import { DealerLaunchOnePerLease } from './pages/DealerLaunchOnePerLease';
import { DealerLaunchLeaseInceptionFees } from './pages/DealerLaunchLeaseInceptionFees';
import { DealerLaunchInstorePrintingSetup } from './pages/DealerLaunchInstorePrintingSetup';
import { DealerLaunchAboutIsIndependent } from './pages/DealerLaunchAboutIsIndependent';
import { DealerLaunchLeaseCreditCopyOver } from './pages/DealerLaunchLeaseCreditCopyOver';
import {
  DealerLaunchIntegrationDealerManagement,
  DealerLaunchIntegrationDealerManagementInfoGate,
} from './pages/DealerLaunchIntegrationDealerManagement';
import { DealerLaunchLendingPromo } from './pages/DealerLaunchLendingPromo';
import { DealerLaunchLendingBusinessInfo } from './pages/DealerLaunchLendingBusinessInfo';
import { DealerLaunchLendingNextSteps } from './pages/DealerLaunchLendingNextSteps';
import {
  completedBusinessInfo,
  completedFinanceContact,
  completedFinanceHours,
  completedLending,
  completedPaymentInfo,
  startedLending,
} from '../../utils/lending';
import { DealerLaunchLendingFinanceContact } from './pages/DealerLaunchLendingFinanceContact';
import { DealerLaunchLendingFinanceHours } from './pages/DealerLaunchLendingFinanceHours';
import { DealerLaunchLendingPayment } from './pages/DealerLaunchLendingPayment';

export const shouldHideSectionOrStep = (
  step: DealerLaunchStep | DealerLaunchSection,
  dealer: Dealer,
) => {
  // hide a step if it's missing required features
  let hideFeature = false;
  // Loop through required features and see if you find one that the dealer doesn't have
  step.requiredFeatures?.some((feature) => {
    // || false is needed just to address typescript
    const hasFeature = dealer.launchpad?.features?.[feature] || false;
    if (!hasFeature) {
      // If it doesn't have the feature, hide it.
      hideFeature = true;
      return true;
    }
    return false;
  });

  let shouldHideStep = false;
  const shouldHideStepFunction = step.shouldHideStep;
  if (shouldHideStepFunction) {
    shouldHideStep = shouldHideStepFunction(dealer);
  }
  return hideFeature || shouldHideStep;
};

export const shouldSkipStep = (step: DealerLaunchStep, dealer: Dealer) => {
  // skip step if it's hidden
  if (shouldHideSectionOrStep(step, dealer)) return true;
  // skip step if it's completed and marked as skip if completed
  return step.skipStepIfComplete && step.isComplete(dealer);
};

/**
 * Makes the property K partial for type T.
 *
 * Example:
 * PartialBy<{ name: string, date: string, time: string }, 'date' | 'time'> =
 *    { name: string, date?: string, time?: string }
 */
type PartialBy<T, K extends keyof T> = Omit<T, K> & Partial<Pick<T, K>>;

/**
 * Helper method for creating steps, needed to handle typing of stepProps
 */
const createStep = <T>(
  step: T extends undefined
    ? Omit<DealerLaunchStep, 'stepProps'>
    : DealerLaunchStep<T>,
): DealerLaunchStep<T> =>
  ({
    stepProps: undefined,
    ...step,
  } as DealerLaunchStep<T>);

/**
 * Helper method for creating step sections, needed to bind steps to the section
 */
const createSection = (
  sectionPart: PartialBy<DealerLaunchSection, 'isComplete' | 'Component'>,
) => {
  const section: DealerLaunchSection = {
    Component: DealerLaunchSectionOverview,
    isComplete: (dealer) =>
      sectionPart.steps.every(
        (step) =>
          shouldHideSectionOrStep(step, dealer) || step.isComplete(dealer),
      ),
    ...sectionPart,
  };
  // update each step for this section
  section.steps.forEach((step) => {
    // bind each step object to the section
    // eslint-disable-next-line no-param-reassign
    step.section = section;

    // merge the required features for the section with the step's required features
    if (section.requiredFeatures) {
      // eslint-disable-next-line no-param-reassign
      step.requiredFeatures = uniq([
        ...(step.requiredFeatures || []),
        ...section.requiredFeatures,
      ]);
    }
  });
  return section;
};

const formatPickupOrDeliveryHours = (days: Days): string => {
  const dayKeys = Object.keys(days);
  return dayKeys
    .filter((day: string): boolean => {
      // recast day to satisfy typecheck.
      const dayKey = day as keyof Days;
      return days[dayKey].enabled;
    })
    .map((day: string): string => {
      // recast day to satisfy typecheck.
      const dayKey = day as keyof Days;
      return `${capitalize(dayKey.substr(0, 3))} ${formatHour(
        days[dayKey].start || 0,
      )} - ${formatHour(days[dayKey].end || 0)}`;
    })
    .join(', ');
};

export const isVerifiedInventory = (dealer: Dealer) =>
  !!dealer.launchpad?.status?.implementerVerifiedInventory;

// if dealer has verified inventory, summary is accessed through "Review" section
// this flag is to hide the navigation link, so that we don't display "Review" twice
export const shouldHideReviewNavigationButton = (dealer: Dealer) =>
  isVerifiedInventory(dealer);

const isPricingValidationComplete = (
  financeValidation?: FinanceTypePricingValidation,
) => {
  const financeTypes = [
    FinanceType.CASH,
    FinanceType.LEASE,
    FinanceType.PURCHASE,
  ];
  return (
    financeTypes?.filter(
      (financeType) =>
        typeof financeValidation?.[financeType]?.correct === 'boolean',
    )?.length === financeTypes.length
  );
};

export const DEALER_LAUNCH_SECTIONS: DealerLaunchSection[] = [
  createSection({
    name: 'About Your Dealership',
    route: ROUTES.DEALER_DASHBOARD_ABOUT,
    steps: [
      createMultiStep(
        {
          name: 'Dealer Information',
          description:
            "Let's have a look at the basics of your dealership, including your name, brand, and logo.",
          route: ROUTES.DEALER_DASHBOARD_ABOUT_INFO,
          getChairmanUrl: CHAIRMAN_ROUTES.DEALER_EDITOR_SPECIFICATIONS,
          isLaunchBlocker: true,
          hideReviewNav: (dealer: Dealer) =>
            shouldHideReviewNavigationButton(dealer),
          getReviewData: (dealer) => [
            { name: 'Name', value: dealer.name ?? null },
            {
              name: 'DealerWebsiteUrl',
              value: dealer.dealerWebsiteUrl ?? null,
            },
            {
              name: 'PrivacyPolicyUrl',
              value: dealer.privacyPolicyUrl ?? null,
            },
            {
              name: 'DealerType',
              value: dealer.dealerType ?? null,
            },
            { name: 'Brand', value: dealer.brand?.join(', ') ?? null },
            { name: 'Logo', value: dealer.logo ? 'Uploaded' : null },
          ],
        },
        [
          createSubStep({
            Component: DealerLaunchAboutIsIndependent,
            isComplete: () => true,
          }),
          createSubStep({
            Component: DealerLaunchAboutInfo,
            isComplete: (dealer) =>
              !!dealer.name &&
              !!dealer.dealerWebsiteUrl &&
              !!dealer.privacyPolicyUrl &&
              !!dealer.brand &&
              !!dealer.brand.length &&
              !!dealer.logo &&
              !!dealer.dealerType,
          }),
        ],
      ),
      createMultiStep(
        {
          name: 'Dealership Contact Information',
          description:
            "Tell us how your customers can best get in touch with you, including the dealership's phone number and address.",
          route: ROUTES.DEALER_DASHBOARD_ABOUT_CONTACT,
          getChairmanUrl: CHAIRMAN_ROUTES.DEALER_EDITOR_SPECIFICATIONS,
          isLaunchBlocker: true,
          hideReviewNav: (dealer: Dealer) =>
            shouldHideReviewNavigationButton(dealer),
          getReviewData: (dealer) => [
            { name: 'Phone', value: dealer.phone ?? null },
            { name: 'Address', value: dealer.address ?? null },
            { name: 'City', value: dealer.city ?? null },
            { name: 'State', value: dealer.state ?? null },
            { name: 'County', value: dealer.county ?? null },
            { name: 'Zip', value: dealer.zip ?? null },
            {
              name: 'Support Contact Email',
              value: dealer.supportEmail ?? null,
            },
            ...(dealer.wizard?.updateEmails?.enabled
              ? [
                  {
                    name: 'Online Deal Notifications',
                    value: dealer.wizard?.updateEmails?.enabled
                      ? 'Enabled'
                      : null,
                  },
                  {
                    name: 'Recipient Email',
                    value: dealer.wizard?.updateEmails?.recipientEmail ?? null,
                  },
                ]
              : [
                  {
                    name: 'Online Deal Notifications',
                    value: dealer.wizard?.updateEmails?.enabled
                      ? 'Enabled'
                      : 'Disabled',
                  },
                ]),
          ],
        },
        [
          createSubStep({
            Component: DealerLaunchAboutContact,
            isComplete: (dealer) => !!dealer.phone && !!dealer.address,
          }),
          createSubStep({
            Component: DealerLaunchAboutSupportEmail,
            isComplete: (dealer) => !!dealer.supportEmail,
          }),
          createSubStep({
            Component: DealerLaunchOnlineDealNotification,
            isComplete: (dealer) =>
              typeof dealer.wizard?.updateEmails?.enabled === 'boolean',
          }),
        ],
      ),
    ],
  }),
  createSection({
    name: 'Connect Your Data Sources',
    route: ROUTES.DEALER_DASHBOARD_INTEGRATIONS,
    steps: [
      createMultiStep(
        {
          name: 'Inventory Feed',
          description:
            "This step exports your dealership's inventory to Upstart Auto Retail.",
          route: ROUTES.DEALER_DASHBOARD_INTEGRATIONS_INVENTORY,
          getChairmanUrl: CHAIRMAN_ROUTES.DEALER_EDITOR_INVENTORY,
          hideReviewNav: (dealer: Dealer) =>
            shouldHideReviewNavigationButton(dealer),
          getReviewData: (dealer) => [
            {
              name: 'Provider',
              value: dealer.launchpad?.inventory?.provider
                ? INVENTORY_TYPE_NAME[dealer.launchpad.inventory.provider] ||
                  startCase(dealer.launchpad.inventory.provider)
                : null,
            },
          ],
        },
        [
          createSubStep({
            Component: DealerLaunchInfoGate,
            stepProps: DealerLaunchIntegrationInventoryInfoGate,
            isComplete: () => true,
          }),
          createSubStep({
            Component: DealerLaunchIntegrationInventory,
            isComplete: (dealer) => !!dealer.launchpad?.inventory?.provider,
          }),
        ],
      ),
      createMultiStep(
        {
          name: 'CRM System',
          description:
            'This step connects Upstart Auto Retail with your CRM, you will need to create Upstart Auto Retail a login to complete.',
          route: ROUTES.DEALER_DASHBOARD_INTEGRATIONS_CRM,
          getChairmanUrl: CHAIRMAN_ROUTES.DEALER_EDITOR_INTEGRATIONS,
          hideReviewNav: (dealer: Dealer) =>
            shouldHideReviewNavigationButton(dealer),
          getReviewData: (dealer) => [
            {
              name: 'Provider',
              value: dealer.crmType
                ? CRM_TYPE_NAME[dealer.crmType as CrmType] ||
                  startCase(dealer.crmType)
                : null,
            },
            ...(dealer.crmType === CrmType.VINSOLUTIONS
              ? [
                  {
                    name:
                      'Enabled Upstart Auto Retail as a partner in VinSolutions',
                    value: dealer.launchpad?.crm
                      ?.enabledVinSolutionsPartnerIntegration
                      ? 'Yes'
                      : 'No',
                  },
                ]
              : []),
          ],
        },
        [
          createSubStep({
            Component: DealerLaunchInfoGate,
            stepProps: DealerLaunchIntegrationCRMInfoGate,
            isComplete: () => true,
          }),
          createSubStep({
            Component: DealerLaunchIntegrationCRM,
            isComplete: (dealer) => !!dealer.crmType,
          }),
          createSubStep({
            Component: DealerLaunchIntegrationADFEmail,
            isComplete: (dealer) =>
              !!dealer.crmEmailNew && !!dealer.crmEmailUsed,
          }),
        ],
      ),
      createMultiStep(
        {
          name: 'Dealer Management System',
          breadcrumbName: 'DMS Provider',
          description:
            'This step helps integrate with your business and determine eligibility for financing products.',
          route: ROUTES.DEALER_DASHBOARD_INTEGRATIONS_DEALER_MANAGEMENT,
          getChairmanUrl: CHAIRMAN_ROUTES.DEALER_EDITOR_INTEGRATIONS,
          hideReviewNav: (dealer: Dealer) =>
            shouldHideReviewNavigationButton(dealer),
          getReviewData: (dealer) => [
            {
              name: 'Provider',
              value: dealer.dmsType
                ? DMS_TYPE_NAME[dealer.dmsType as DMSType] ||
                  startCase(dealer.dmsType)
                : null,
            },
          ],
        },
        [
          createSubStep({
            Component: DealerLaunchInfoGate,
            stepProps: DealerLaunchIntegrationDealerManagementInfoGate,
            isComplete: () => true,
          }),
          createSubStep({
            Component: DealerLaunchIntegrationDealerManagement,
            isComplete: (dealer) => !!dealer.dmsType,
          }),
        ],
      ),
      createMultiStep(
        {
          name: 'F&I System',
          description: 'This step connects to your F&I System.',
          route: ROUTES.DEALER_DASHBOARD_INTEGRATIONS_FNI,
          getChairmanUrl: CHAIRMAN_ROUTES.DEALER_EDITOR_INTEGRATIONS,
          hideReviewNav: (dealer: Dealer) =>
            shouldHideReviewNavigationButton(dealer),
          getReviewData: (dealer) => [
            {
              name: 'Provider',
              value: dealer.fniType
                ? FNI_TYPE_NAME[dealer.fniType as FNIType] ||
                  startCase(dealer.fniType)
                : null,
            },
          ],
        },
        [
          createSubStep({
            Component: DealerLaunchInfoGate,
            stepProps: DealerLaunchIntegrationFnIInfoGate,
            isComplete: () => true,
          }),
          createSubStep({
            Component: DealerLaunchIntegrationFnI,
            isComplete: (dealer) => !!dealer.fniType,
          }),
        ],
      ),
      createMultiStep(
        {
          name: 'Trade-in System',
          description: 'This step connects to your trade-in tool.',
          route: ROUTES.DEALER_DASHBOARD_INTEGRATIONS_TRADEIN_MANAGER,
          getChairmanUrl: CHAIRMAN_ROUTES.DEALER_EDITOR_INTEGRATIONS,
          hideReviewNav: (dealer: Dealer) =>
            shouldHideReviewNavigationButton(dealer),
          getReviewData: (dealer) => [
            {
              name: 'Provider',
              value: dealer.tradeInManagerType
                ? TRADEIN_MANAGER_TYPE_NAME[
                    dealer.tradeInManagerType as TradeInType
                  ] || startCase(dealer.tradeInManagerType)
                : null,
            },
          ],
        },
        [
          createSubStep({
            Component: DealerLaunchInfoGate,
            stepProps: DealerLaunchIntegrationTradeinManagerInfoGate,
            isComplete: () => true,
          }),
          createSubStep({
            Component: DealerLaunchIntegrationTradeinManager,
            isComplete: (dealer) =>
              !!dealer.launchpad?.status?.finishedTradeInManagerSelection,
          }),
        ],
      ),
    ],
  }),
  createSection({
    name: 'Lending',
    route: ROUTES.DEALER_DASHBOARD_LENDING,
    steps: [
      createStep({
        name: 'Promo',
        Component: DealerLaunchLendingPromo,
        hideStepNav: true,
        route: ROUTES.DEALER_DASHBOARD_LENDING_PROMO,
        isComplete: (dealer) => startedLending(dealer),
      }),
      createStep({
        name: 'Business Info',
        description:
          'Tell us your basic business info for legal and compliance.',
        Component: DealerLaunchLendingBusinessInfo,
        route: ROUTES.DEALER_DASHBOARD_LENDING_BUSINESS,
        isComplete: (dealer) => completedBusinessInfo(dealer),
      }),
      createStep({
        name: 'Finance Contact',
        description:
          'Tell us how to get in touch with your finance office for any questions.',
        Component: DealerLaunchLendingFinanceContact,
        route: ROUTES.DEALER_DASHBOARD_LENDING_FINANCE_CONTACT,
        isComplete: (dealer) => completedFinanceContact(dealer),
      }),
      createStep({
        name: 'Business Hours',
        description: 'Tell us the best time to call you for any questions.',
        Component: DealerLaunchLendingFinanceHours,
        route: ROUTES.DEALER_DASHBOARD_LENDING_FINANCE_HOURS,
        isComplete: (dealer) => completedFinanceHours(dealer),
      }),
      createStep({
        name: 'Payment Info',
        Component: DealerLaunchLendingPayment,
        description: 'Tell us how to pay you.',
        route: ROUTES.DEALER_DASHBOARD_LENDING_PAYMENT,
        isComplete: (dealer) => completedPaymentInfo(dealer),
      }),
      createStep({
        name: 'Next Steps',
        Component: DealerLaunchLendingNextSteps,
        route: ROUTES.DEALER_DASHBOARD_LENDING_NEXT,
        isComplete: (dealer) => completedLending(dealer),
      }),
    ],
  }),
  createSection({
    name: 'Pricing',
    route: ROUTES.DEALER_DASHBOARD_PRICING,
    steps: [
      createStep({
        name: 'Fees',
        description:
          'Configure the fees that will be applied to your vehicle sales.',
        Component: DealerLaunchPricingFees,
        route: ROUTES.DEALER_DASHBOARD_PRICING_FEES,
        getChairmanUrl: CHAIRMAN_ROUTES.DEALER_EDITOR_FEES,
        isComplete: (dealer) => !isEmpty(dealer.fees),
        hideReviewNav: (dealer: Dealer) =>
          shouldHideReviewNavigationButton(dealer),
        getReviewData: (dealer) => [
          {
            name: 'Fee Count',
            value: !isEmpty(dealer.fees)
              ? `${
                  flattenDeep(
                    Object.values(dealer.fees || {}).map((f) =>
                      Object.values(f || {}).map((f2) =>
                        Object.values(f2 || {}),
                      ),
                    ),
                  ).length
                } saved`
              : null,
          },
        ],
      }),
      createStep({
        name: 'Set Source Price',
        description: 'Tell us what price to use from your inventory feed.',
        Component: DealerLaunchPricingSource,
        route: ROUTES.DEALER_DASHBOARD_PRICING_SOURCE,
        getChairmanUrl: CHAIRMAN_ROUTES.DEALER_PRICING_EDITOR_PRICING,
        isComplete: (dealer) =>
          !!getNewInstorePriceSource(dealer) ||
          !!getUsedInstorePriceSource(dealer),
        hideReviewNav: (dealer: Dealer) =>
          shouldHideReviewNavigationButton(dealer),
        getReviewData: (dealer) => [
          {
            name: 'New Car Price Source',
            value: getNewInstorePriceSource(dealer)
              ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                PRICE_SOURCE_NAME[getNewInstorePriceSource(dealer)!]
              : null,
          },
          {
            name: 'Used Car Price Source',
            value: getNewInstorePriceSource(dealer)
              ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                PRICE_SOURCE_NAME[getUsedInstorePriceSource(dealer)!]
              : null,
          },
        ],
      }),
      createMultiStep(
        {
          name: 'Payment Configuration',
          description: 'Configure desking payment',
          route: ROUTES.DEALER_DASHBOARD_PRICING_PAYMENT_CONFIGURATION,
          getChairmanUrl: CHAIRMAN_ROUTES.DEALER_EDITOR_CONFIGURATION,
          hideReviewNav: (dealer: Dealer) =>
            shouldHideReviewNavigationButton(dealer),
        },
        [
          createSubStep({
            Component: DealerLaunchDeskingModel,
            isComplete: (dealer) =>
              typeof dealer?.desking?.defaultPencil === 'boolean' &&
              typeof dealer?.desking?.managerDesking === 'boolean' &&
              typeof dealer?.desking?.salespersonDesking === 'boolean',
          }),
          createSubStep({
            Component: DealerLaunchTradeInTaxCredit,
            stepProps: {
              type: Condition.NEW,
            },
            isComplete: (dealer) =>
              typeof dealer.desking?.useTradeInAllowanceForTax?.New ===
              'boolean',
            skipStep: (dealer) => dealer?.isIndependent || false,
          }),
          createSubStep({
            Component: DealerLaunchTradeInTaxCredit,
            stepProps: {
              type: Condition.USED,
            },
            isComplete: (dealer) =>
              typeof dealer.desking?.useTradeInAllowanceForTax?.Used ===
              'boolean',
          }),
          createSubStep({
            Component: DealerLaunchLeaseInceptionFees,
            isComplete: (dealer) =>
              !!dealer.desking?.inceptionFeesTotalAtStart?.taxes &&
              !!dealer.desking?.inceptionFeesTotalAtStart?.fees &&
              !!dealer.desking?.inceptionFeesTotalAtStart?.firstMonth,
            skipStep: (dealer) => dealer?.isIndependent || false,
          }),
          createSubStep({
            Component: DealerLaunchMaximumResidual,
            isComplete: (dealer) =>
              dealer.desking?.ignoreMRM === true ||
              dealer.desking?.ignoreMRM === false,
            skipStep: (dealer) => dealer?.isIndependent || false,
          }),
          createSubStep({
            Component: DealerLaunchApplyRebates,
            isComplete: (dealer) =>
              dealer.desking?.applyRebatesToDueAtSigning === true ||
              dealer.desking?.applyRebatesToDueAtSigning === false,
          }),
          createSubStep({
            Component: DealerLaunchOnePerLease,
            isComplete: (dealer) =>
              dealer.desking?.enableOnePayLease === true ||
              dealer.desking?.enableOnePayLease === false,
            skipStep: (dealer) => dealer?.isIndependent || false,
          }),
        ],
      ),
      createMultiStep(
        {
          name: 'Credit Tiers',
          description: 'Define credit values for different tiers.',
          route: ROUTES.DEALER_DASHBOARD_PRICING_CREDIT,
          getChairmanUrl:
            CHAIRMAN_ROUTES.DEALER_PRICING_EDITOR_ONLINE_APR_CONFIG,
          hideReviewNav: (dealer: Dealer) =>
            shouldHideReviewNavigationButton(dealer),
          getReviewData: (dealer) => {
            let reviewData: { name: string; value: string | null }[] = [];
            const purchaseTiers =
              dealer.launchpad?.creditPricing?.[FinanceType.PURCHASE]
                ?.creditTiers;
            const leaseTiers =
              dealer.launchpad?.creditPricing?.[FinanceType.LEASE]?.creditTiers;
            if (!purchaseTiers && !leaseTiers) {
              return [{ name: '', value: null }];
            }
            if (purchaseTiers) {
              reviewData = purchaseTiers.map((tier, idx) => ({
                name: `Finance ${tier.name}`,
                value: `${tier.value} - ${
                  idx === 0 ? CreditRatingMax : purchaseTiers[idx - 1].value - 1
                }`,
              }));
            }
            if (leaseTiers) {
              reviewData = reviewData.concat(
                leaseTiers.map((tier, idx) => ({
                  name: `Lease ${tier.name}`,
                  value: `${tier.value} - ${
                    idx === 0 ? CreditRatingMax : leaseTiers[idx - 1].value - 1
                  }`,
                })),
              );
            }
            return reviewData;
          },
        },
        [
          createSubStep({
            Component: DealerLaunchFinancePricingCredit,
            isComplete: (dealer) =>
              !!dealer.launchpad?.creditPricing?.[FinanceType.PURCHASE]
                ?.creditTiers?.length,
          }),
          createSubStep({
            Component: DealerLaunchLeaseCreditCopyOver,
            isComplete: (dealer) =>
              !!dealer.launchpad?.status?.finishedPricingLeaseCopyOver ||
              !!dealer.isIndependent,
            skipStep: (dealer) =>
              !!dealer.launchpad?.status?.finishedPricingLeaseCopyOver ||
              !!dealer?.isIndependent,
          }),
          createSubStep({
            Component: DealerLaunchLeasePricingCredit,
            isComplete: (dealer) =>
              !!dealer.launchpad?.creditPricing?.[FinanceType.LEASE]
                ?.creditTiers?.length || !!dealer.isIndependent,
            skipStep: (dealer) => !!dealer?.isIndependent,
          }),
        ],
      ),
      createStep({
        name: 'APR Configuration',
        description:
          'Customize the APR for different finance terms being applied to vehicle sales.',
        Component: DealerLaunchFinancePricingRates,
        route: ROUTES.DEALER_DASHBOARD_PRICING_FINANCE_RATES,
        getChairmanUrl: CHAIRMAN_ROUTES.DEALER_PRICING_EDITOR_ONLINE_APR_CONFIG,
        isComplete: (dealer) => {
          const creditTiersLength =
            dealer.launchpad?.creditPricing?.[FinanceType.PURCHASE]?.creditTiers
              ?.length;
          const pricingLength =
            dealer.launchpad?.creditPricing?.[FinanceType.PURCHASE]?.pricing
              ?.length;
          return !!(creditTiersLength && creditTiersLength === pricingLength);
        },
        hideReviewNav: (dealer: Dealer) =>
          shouldHideReviewNavigationButton(dealer),
        getReviewData: (dealer) => {
          const pricing =
            dealer.launchpad?.creditPricing?.[FinanceType.PURCHASE]?.pricing;
          const terms =
            dealer.launchpad?.creditPricing?.[FinanceType.PURCHASE]?.terms;
          const creditTiers =
            dealer.launchpad?.creditPricing?.[FinanceType.PURCHASE]
              ?.creditTiers;
          if (!pricing || !terms || !creditTiers) {
            return [{ name: '', value: null }];
          }

          return flatten([
            ...terms.map((term, index) => {
              const month = term.months;
              return [
                {
                  name: `Term Option ${['One', 'Two', 'Three'][index]}`,
                  value: month === undefined ? null : `${month} Months`,
                },
                ...creditTiers.map((tier, creditIdx) => {
                  const { apr } = pricing[creditIdx][index];
                  return {
                    name: `${tier.name} APR`,
                    value: apr === undefined ? null : `${apr}%`,
                    nameClassName: 'review-item-indented',
                  };
                }),
              ];
            }),
          ]);
        },
      }),
      createStep({
        name: 'Lease Rates Configuration',
        description:
          'Customize the lease rate for different vehicle lease terms.',
        Component: DealerLaunchPricingRatesLease,
        route: ROUTES.DEALER_DASHBOARD_PRICING_LEASE_RATES,
        getChairmanUrl: CHAIRMAN_ROUTES.DEALER_PRICING_EDITOR_ONLINE_APR_CONFIG,
        shouldHideStep: (dealer: Dealer) => dealer?.isIndependent || false,
        isComplete: (dealer) => {
          const creditTiersLength =
            dealer.launchpad?.creditPricing?.[FinanceType.LEASE]?.creditTiers
              ?.length;
          const pricingLength =
            dealer.launchpad?.creditPricing?.[FinanceType.LEASE]?.pricing
              ?.length;
          return !!(creditTiersLength && creditTiersLength === pricingLength);
        },
        hideReviewNav: (dealer: Dealer) =>
          shouldHideReviewNavigationButton(dealer),
        getReviewData: (dealer) => {
          const pricing =
            dealer.launchpad?.creditPricing?.[FinanceType.LEASE]?.pricing;
          const terms =
            dealer.launchpad?.creditPricing?.[FinanceType.LEASE]?.terms;
          const creditTiers =
            dealer.launchpad?.creditPricing?.[FinanceType.LEASE]?.creditTiers;
          if (!pricing || !terms || !creditTiers) {
            return [{ name: '', value: null }];
          }

          const mileages = Array.from(new Set(terms.map((t) => t.mileage)));
          const months = Array.from(new Set(terms.map((t) => t.months)));

          return flatten([
            ...mileages.map((mileage, mileageIndex) => {
              const nameValues: {
                name: string;
                value: string | null;
                nameClassName: string;
              }[] = [];

              months.forEach((month, termIndex) => {
                creditTiers.forEach((tier, tierIdx) => {
                  const basisPoints =
                    pricing[tierIdx][termIndex * mileages.length + mileageIndex]
                      .moneyFactor;
                  nameValues.push({
                    name: `${month} Months - ${tier.name} Markup`,
                    value:
                      basisPoints === undefined
                        ? null
                        : `${basisToMoneyDecimalConversion(basisPoints)}`,
                    nameClassName: 'review-item-indented',
                  });
                });
              });

              return [
                {
                  name: `Mileage Option ${
                    ['One', 'Two', 'Three', 'Four', 'Five'][mileageIndex]
                  }`,
                  value:
                    mileage === undefined || mileage === null
                      ? null
                      : `${mileage} Miles`,
                },
                ...nameValues,
              ];
            }),
          ]);
        },
      }),
    ],
  }),
  createSection({
    name: 'Website Options',
    route: ROUTES.DEALER_DASHBOARD_ONLINE,
    requiredFeatures: ['online'],
    steps: [
      createMultiStep(
        {
          name: 'Pickup & Delivery',
          description:
            'Tell us what hours your customers can come for pick-up and delivery.',
          route: ROUTES.DEALER_DASHBOARD_ONLINE_PICKUP_DELIVERY,
          getChairmanUrl: CHAIRMAN_ROUTES.DEALER_EDITOR_CONFIGURATION,
          isLaunchBlocker: true,
          hideReviewNav: (dealer: Dealer) =>
            shouldHideReviewNavigationButton(dealer),
          getReviewData: (dealer) => [
            ...(dealer.wizard?.pickup
              ? [
                  {
                    name: 'Pickup Hours',
                    value: formatPickupOrDeliveryHours(
                      dealer.wizard.pickup.days as Days,
                    ),
                  },
                  {
                    name: 'Earliest pickup',
                    value: `${
                      dealer.wizard.pickup.startsAfter || 0
                    } after purchase`,
                  },
                  {
                    name: 'Latest pickup',
                    value: `${
                      dealer.wizard.pickup.endsAfter || 0
                    } after purchase`,
                  },
                  {
                    name: 'Appointment Window',
                    value: `${
                      dealer.wizard.pickup.appointmentWindow || 0
                    } minutes`,
                  },
                ]
              : [
                  {
                    name: 'Pickup',
                    value: null,
                  },
                ]),
            ...(dealer.wizard?.delivery?.enabled
              ? [
                  {
                    name: 'Delivery Hours',
                    value: formatPickupOrDeliveryHours(
                      dealer.wizard.delivery.days as Days,
                    ),
                  },
                  {
                    name: 'Earliest delivery',
                    value: `${
                      dealer.wizard.delivery.startsAfter || 0
                    } after purchase`,
                  },
                  {
                    name: 'Latest delivery',
                    value: `${
                      dealer.wizard.delivery.endsAfter || 0
                    } after purchase`,
                  },
                  {
                    name: 'Delivery radius',
                    value: `${dealer.wizard.delivery.radius || 0} miles`,
                  },
                ]
              : [
                  {
                    name: 'Delivery',
                    value:
                      dealer.wizard?.delivery?.enabled === false
                        ? 'Disabled'
                        : null,
                  },
                ]),
          ],
        },
        [
          createSubStep({
            Component: DealerLaunchOnlinePickupDelivery,
            stepProps: { type: 'pickup' as const },
            isComplete: (dealer) => !!dealer.wizard?.pickup,
          }),
          createSubStep({
            Component: DealerLaunchOnlinePickupDelivery,
            stepProps: { type: 'delivery' as const },
            isComplete: (dealer) => !!dealer.wizard?.delivery,
          }),
        ],
      ),
      createStep({
        name: 'Widgets',
        description:
          'This step allows you to change the button colors and button text for the Online Upstart Auto Retail solution for your website.',
        Component: DealerLaunchOnlineWidgets,
        route: ROUTES.DEALER_DASHBOARD_ONLINE_WIDGETS,
        getChairmanUrl: CHAIRMAN_ROUTES.DEALER_WIDGETS,
        isComplete: (dealer) =>
          !!dealer.launchpad?.status?.finishedWidgetConfig,
        hideReviewNav: (dealer: Dealer) =>
          shouldHideReviewNavigationButton(dealer),
        getReviewData: (dealer, extras) => [
          ...(dealer.launchpad?.status?.finishedWidgetConfig
            ? [
                {
                  name: 'Main CTA Text',
                  value:
                    extras?.widgetConfig?.config.VDP.leadCaptureButton.text ||
                    'Loading...',
                },
                {
                  name: 'Color',
                  value:
                    extras?.widgetConfig?.config.VDP.leadCaptureButton
                      .backgroundColor || 'Loading...',
                },
                {
                  name: 'Border Radius',
                  value:
                    extras?.widgetConfig?.config.VDP.leadCaptureButton
                      .radius !== undefined
                      ? `${extras.widgetConfig.config.VDP.leadCaptureButton.radius}px`
                      : 'Loading...',
                },
              ]
            : [
                {
                  name: '',
                  value: null,
                },
              ]),
        ],
      }),
      createStep({
        name: 'Virtual Showroom',
        description:
          'Enable virtual showroom to connect online customers to your showroom',
        Component: DealerLaunchOnlineVirtualShowroom,
        route: ROUTES.DEALER_DASHBOARD_ONLINE_VIRTUAL_SHOWROOM,
        requiredFeatures: ['instore'],
        getChairmanUrl: CHAIRMAN_ROUTES.DEALER_EDITOR_CONFIGURATION,
        isComplete: (dealer) =>
          dealer.featureConfig?.virtualShowroom?.enabled !== undefined,
        hideReviewNav: (dealer: Dealer) =>
          shouldHideReviewNavigationButton(dealer),
        getReviewData: (dealer) => [
          {
            name: 'Enabled',
            value:
              // eslint-disable-next-line no-nested-ternary
              dealer.featureConfig?.virtualShowroom?.enabled !== undefined
                ? dealer.featureConfig?.virtualShowroom?.enabled
                  ? `Yes`
                  : `No`
                : null,
          },
        ],
      }),
    ],
  }),
  createSection({
    name: 'In Store Options',
    route: ROUTES.DEALER_DASHBOARD_INSTORE,
    requiredFeatures: ['instore'],
    steps: [
      createStep({
        name: 'Forms Library',
        description:
          'This step allows you to choose what forms you would like to have on the Upstart Auto Retail In-Store solution.',
        Component: DealerLaunchInstoreForms,
        route: ROUTES.DEALER_DASHBOARD_INSTORE_FORMS,
        getChairmanUrl: CHAIRMAN_ROUTES.DEALER_USERS,
        isComplete: (dealer) => !!dealer.launchpad?.status?.finishedFormsConfig,
        hideReviewNav: (dealer: Dealer) =>
          shouldHideReviewNavigationButton(dealer),
        getReviewData: (dealer, extras) =>
          dealer.launchpad?.status?.finishedFormsConfig
            ? [
                {
                  name: 'Forms Count',
                  value: `${extras?.forms?.length || 0} saved`,
                },
              ]
            : [{ name: '', value: null }],
      }),
      createStep({
        name: 'Printing',
        description: 'This step allows you to choose printing setup',
        Component: DealerLaunchInstorePrintingSetup,
        route: ROUTES.DEALER_DASHBOARD_INSTORE_PRINTING_SETUP,
        getChairmanUrl: CHAIRMAN_ROUTES.DEALER_USERS,
        isComplete: (dealer) =>
          !!dealer.launchpad?.status?.finishedPrintingSetup,
        hideReviewNav: (dealer: Dealer) =>
          shouldHideReviewNavigationButton(dealer),
        getReviewData: (dealer) =>
          dealer.launchpad?.status?.finishedPrintingSetup
            ? [
                {
                  name: 'Is Airprint Supported',
                  value: dealer.launchpad.printSetup?.isAirprintSupported
                    ? 'Yes'
                    : 'No',
                },
                {
                  name: 'Is On Same Wifi Network',
                  value: dealer.launchpad.printSetup?.isOnSameWifiNetwork
                    ? 'Yes'
                    : 'No',
                },
                {
                  name: 'Has Form Library Selected',
                  value: dealer.launchpad.printSetup?.hasFormLibrarySelected
                    ? 'Yes'
                    : 'No',
                },
                {
                  name: 'Is Print Ready',
                  value: dealer.launchpad.printSetup?.isPrintReady
                    ? 'Yes'
                    : 'No',
                },
                {
                  name: 'Has Print Selected',
                  value: dealer.launchpad.printSetup?.hasTestPrintSelected
                    ? 'Yes'
                    : 'No',
                },
              ]
            : [{ name: '', value: null }],
      }),
    ],
  }),
  createSection({
    name: 'Add Users',
    route: ROUTES.DEALER_DASHBOARD_USERS,
    requiredFeatures: ['instore'],
    steps: [
      createStep({
        name: 'Add Users',
        description:
          'Add contact info to invite people to join your account on Upstart Auto Retail',
        Component: DealerLaunchInstoreUsers,
        route: ROUTES.DEALER_DASHBOARD_USERS_ADD,
        getChairmanUrl: CHAIRMAN_ROUTES.DEALER_USERS,
        isComplete: (dealer) => !!dealer.launchpad?.status?.finishedUsersConfig,
        hideReviewNav: (dealer: Dealer) =>
          shouldHideReviewNavigationButton(dealer),
        getReviewData: (dealer, extras) => [
          {
            name: 'User Count',
            value: extras?.users?.length
              ? // -1 to account for the current user
                `${extras?.users?.length - 1} saved`
              : null,
          },
        ],
      }),
    ],
  }),
  createSection({
    name: 'Review',
    route: ROUTES.DEALER_REVIEW_VALIDATION,
    hideInReviewSummary: true,
    // This section is only hidden when dealer has not verified inventory
    // Review summary will be accessible via button on the navigation sidebar
    // as opposed to a step inside a section (when dealer has verified inventory)
    shouldHideStep: (dealer) => !isVerifiedInventory(dealer),
    steps: [
      createMultiStep(
        {
          name: 'New Vehicle Pricing Validation',
          description: 'Validate new vehicle pricing configuration.',
          route: ROUTES.DEALER_DASHBOARD_PRICING_VALIDATION,
          getChairmanUrl: CHAIRMAN_ROUTES.DEALER_EDITOR_FEES,
          hideSkipStep: true,
          hideReviewNav: (dealer: Dealer) =>
            !!dealer.launchpad?.status?.implementerVerifiedInventory,
          shouldHideStep: (dealer: Dealer) =>
            !dealer.launchpad?.status?.implementerVerifiedInventory,
        },
        [
          createSubStep({
            Component: DealerLaunchPricingValidation,
            isComplete: (dealer) =>
              !dealer.launchpad?.features?.online ||
              isPricingValidationComplete(
                dealer.launchpad?.pricingValidation?.New?.Online,
              ),
            stepProps: {
              validationType: PricingValidationType.NEW,
              clientType: PricingValidationClientType.ONLINE,
            },
            skipStep: (dealer: Dealer) => !dealer.launchpad?.features?.online,
          }),
          createSubStep({
            Component: DealerLaunchPricingValidation,
            isComplete: (dealer) =>
              !dealer.launchpad?.features?.instore ||
              isPricingValidationComplete(
                dealer.launchpad?.pricingValidation?.New?.Instore,
              ),
            stepProps: {
              validationType: PricingValidationType.NEW,
              clientType: PricingValidationClientType.INSTORE,
            },
            skipStep: (dealer: Dealer) => !dealer.launchpad?.features?.instore,
          }),
        ],
      ),
      createMultiStep(
        {
          name: 'Used Vehicle Pricing Validation',
          description: 'Validate used vehicle pricing configuration.',
          route: ROUTES.DEALER_DASHBOARD_PRICING_VALIDATION_USED,
          getChairmanUrl: CHAIRMAN_ROUTES.DEALER_EDITOR_FEES,
          hideSkipStep: true,
          shouldHideStep: (dealer: Dealer) =>
            !dealer.launchpad?.status?.implementerVerifiedInventory,
          hideReviewNav: (dealer: Dealer) =>
            !!dealer.launchpad?.status?.implementerVerifiedInventory,
        },
        [
          createSubStep({
            Component: DealerLaunchPricingValidation,
            isComplete: (dealer) =>
              !dealer.launchpad?.features?.online ||
              isPricingValidationComplete(
                dealer.launchpad?.pricingValidation?.Used?.Online,
              ),
            stepProps: {
              validationType: PricingValidationType.Used,
              clientType: PricingValidationClientType.ONLINE,
            },
            skipStep: (dealer: Dealer) => !dealer.launchpad?.features?.online,
          }),
          createSubStep({
            Component: DealerLaunchPricingValidation,
            isComplete: (dealer) =>
              !dealer.launchpad?.features?.instore ||
              isPricingValidationComplete(
                dealer.launchpad?.pricingValidation?.Used?.Instore,
              ),
            stepProps: {
              validationType: PricingValidationType.Used,
              clientType: PricingValidationClientType.INSTORE,
              isLastPricingValidationForm: true,
            },
            skipStep: (dealer: Dealer) => !dealer.launchpad?.features?.instore,
          }),
        ],
      ),
      createStep({
        name: 'Review',
        Component: DealerLaunchReview,
        route: ROUTES.DEALER_DASHBOARD_REVIEW,
        isComplete: (dealer: Dealer) =>
          !!dealer.launchpad?.status?.readyToLaunch,
        hideSkipStep: true,
        hideSectionNav: false,
        hideHomeNav: false,
        hideReviewNav: (dealer: Dealer) =>
          !!dealer.launchpad?.status?.implementerVerifiedInventory,
      }),
    ],
  }),
];

export const getActiveDealerLaunchSections = (dealer: Dealer) =>
  DEALER_LAUNCH_SECTIONS.filter(
    (section) => !shouldHideSectionOrStep(section, dealer),
  );

export const isProductSelectComplete = (dealer: Dealer) =>
  !!dealer.launchpad?.status?.finishedProductSelect;

export const isReadyToLaunch = (dealer: Dealer) =>
  !!dealer.launchpad?.status?.readyToLaunch;

export const DEALER_LAUNCH_REVIEW_STEP: DealerLaunchStep = createStep({
  name: 'Review',
  Component: DealerLaunchReview,
  route: ROUTES.DEALER_DASHBOARD_REVIEW,
  isComplete: isReadyToLaunch,
  hideSkipStep: true,
  hideSectionNav: false,
  hideHomeNav: false,
  hideReviewNav: false,
});

/**
 * Step shown after dealer submits details for launch and are locked out
 */
export const DEALER_LAUNCH_COMPLETE_STEP: DealerLaunchStep = createStep({
  name: 'Review',
  Component: DealerLaunchComplete,
  route: ROUTES.DEALER_DASHBOARD_REVIEW,
  isComplete: isReadyToLaunch,
  hideSkipStep: true,
  hideSectionNav: true,
  hideHomeNav: true,
});

export const DEALER_LAUNCH_STEPS: DealerLaunchStep[] = [
  createStep({
    name: 'Home',
    Component: DealerLaunchHome,
    route: ROUTES.DEALER_DASHBOARD_HOME,
    isComplete: isProductSelectComplete,
    hideSectionNav: (dealer) => !isProductSelectComplete(dealer),
    hideReviewNav: (dealer) =>
      !isProductSelectComplete(dealer) ||
      shouldHideReviewNavigationButton(dealer),
    hideSkipStep: true,
  }),
  createStep({
    name: 'Product Select',
    Component: DealerLaunchProductSelect,
    route: ROUTES.DEALER_DASHBOARD_PRODUCT_SELECT,
    isComplete: isProductSelectComplete,
    hideSectionNav: true,
    hideReviewNav: true,
    hideSkipStep: true,
    skipStepIfComplete: true,
  }),
  ...flatten(DEALER_LAUNCH_SECTIONS.map((section) => section.steps)),
  DEALER_LAUNCH_REVIEW_STEP,
];

/**
 * Retrieves the next step to display
 *
 * @param currentIndex the index to start searching from
 * @param dealer the dealer that the steps are being shown for
 * @param skipCompleteSteps whether complete steps should be skipped over
 */
export const getNextStep = ({
  currentIndex,
  dealer,
  filteredSteps,
  options,
}: {
  currentIndex: number;
  dealer: Dealer;
  filteredSteps?: DealerLaunchStep[];
  options?: { skipCompleteSteps?: boolean };
}) => {
  const steps = filteredSteps || DEALER_LAUNCH_STEPS;
  // iterate until the next step
  let nextStepIndex = currentIndex + 1;
  while (
    nextStepIndex < steps.length &&
    (shouldSkipStep(steps[nextStepIndex], dealer) ||
      (options?.skipCompleteSteps && steps[nextStepIndex].isComplete(dealer)))
  ) {
    nextStepIndex += 1;
  }
  return steps[nextStepIndex];
};

export const getNextIncompleteStep = (
  section: DealerLaunchSection,
  dealer: Dealer,
  filteredSteps?: Array<DealerLaunchStep>,
) => {
  // Get the next step needed to complete
  // iterate until a step is found that is not complete or until end is reached
  // Works within sections only
  let continueSectionIndex = 0;
  const steps = filteredSteps || section?.steps;
  while (
    continueSectionIndex < steps.length &&
    steps[continueSectionIndex].isComplete(dealer)
  ) {
    continueSectionIndex += 1;
  }
  // Returns length (above index) if all steps are complete
  return continueSectionIndex;
};

/**
 * Returns the next non-hidden section after the given section, or undefined if no such section exists
 */
export const getNextSection = ({
  currentSection,
  dealer,
  filteredSections,
}: {
  currentSection: DealerLaunchSection;
  dealer: Dealer;
  filteredSections?: DealerLaunchSection[];
}) => {
  // get the index of the current section
  const sections = filteredSections || DEALER_LAUNCH_SECTIONS;
  const currentSectionIndex = sections.findIndex(
    (section) => section === currentSection,
  );
  if (currentSectionIndex === -1)
    throw new Error('Failed to find current section');

  // get the first non-hidden section after the current section index
  return sections.find(
    (section, index) =>
      index > currentSectionIndex && !shouldHideSectionOrStep(section, dealer),
  );
};

export const filterDealerLaunchSteps = (dealer: Dealer | null | undefined) => {
  if (!dealer) {
    return DEALER_LAUNCH_STEPS;
  }
  return DEALER_LAUNCH_STEPS.filter(
    (step) => !shouldHideSectionOrStep(step, dealer),
  );
};

export const filterDealerLaunchSections = (
  dealer: Dealer | null | undefined,
) => {
  if (!dealer) {
    return DEALER_LAUNCH_SECTIONS;
  }
  return DEALER_LAUNCH_SECTIONS.filter(
    (step) => !shouldHideSectionOrStep(step, dealer),
  );
};

/**
 * Gets the percent of steps completed
 */
export const getStepCompletePercent = (dealer: Dealer) => {
  const activeSteps = filterDealerLaunchSteps(dealer);
  const total = activeSteps.length || 1;
  const complete = activeSteps.filter((step) => step.isComplete(dealer)).length;
  return complete / total;
};
